/**
 * Trigger Detail Page (Phase 12.2)
 *
 * Unified results view for a single assessment trigger.
 * Shows SOP assessment, KPI measurements, and observation results
 * all linked to one trigger — regardless of trigger type.
 *
 * Phase 41A: the header names the camera, lens, template and KPI and shows
 * when the clip was filmed, in the org's time zone. The KPI section lists one
 * entry per KPI run, each saved result with its Why panel.
 *
 * Phase 41B: the trigger's head-blurred clip plays below the KPI section.
 */

import { useMemo, useState } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Clock,
  Camera,
  User,
  FileText,
  CheckCircle2,
  XCircle,
  Loader2,
  AlertTriangle,
  BarChart3,
  MessageSquare,
  Video,
  VideoOff,
  Target,
  Shield,
  Zap,
  Layers,
  ExternalLink,
  ChevronDown,
  CircleDashed,
  ClipboardList,
} from 'lucide-react';
import { useTriggerDetail } from '../../hooks/useAssessments';
import { useKpiDefinitions } from '../../hooks/useKpi';
import { useCurrentUser } from '../../hooks/useUsers';
import { getScoreColorSet } from '../../lib/score-colors';
import { useDocumentTitle } from '../../lib/hooks/useDocumentTitle';
import { isSafeInAppPath } from '../../lib/return-to';
import {
  formatFootageWindow,
  formatKpiValue,
  hasWhyPanel,
  isFlagged,
  kpiRunBadge,
  kpiRunMessage,
  noValueLabel,
  rowObservationStatus,
  whyHeadline,
  type KpiRunBadgeTone,
} from '../../lib/kpi-format';
import { KpiWhyPanel } from '../../components/kpi/KpiWhyPanel';
import type { KpiAggregation, KpiRun, KpiWhy } from '@guild/shared';

/**
 * One item of the trigger's `kpiMeasurements` (spec §4.2.2). Phase 41A fields
 * are optional: during a promote this page can meet the previous API for a few
 * minutes (spec S16), which sends `measurementData` instead.
 */
interface TriggerKpiMeasurement {
  kpiName: string;
  kpiCode: string;
  primaryValue: number | null;
  thresholdStatus: string | null;
  unit: string | null;
  displayFormat?: string | null;
  kpiDefinitionId?: string;
  observationStatus?: string | null;
  why?: KpiWhy;
  /** Sent only by APIs from before spec D12. */
  measurementData?: unknown;
}

/**
 * A KPI run. `state` and `reasonCode` are any string: a newer API may send
 * states or reasons this page does not know (S16), which read as defaults.
 */
type TriggerKpiRun = Omit<KpiRun, 'state' | 'reasonCode'> & {
  state: string;
  reasonCode?: string | null;
};

// Trigger data shape from API. Phase 41A fields are optional and read through
// defaults (spec S16): the previous API omits them.
interface TriggerData {
  id: string;
  status: string;
  triggerType: string;
  startTime: string;
  endTime: string;
  cameraId: string | null;
  sourceNumber: number | null;
  operatorId: string | null;
  sopId: string | null;
  assessmentId: string | null;
  errorMessage: string | null;
  managerNotes: string | null;
  /** The AI's free text: sent to admin+ only (spec D13), so absent for other roles. */
  observationSummary?: string | null;
  detectedSectionId: string | null;
  detectedSectionTitle: string | null;
  detectedSectionConfidence: number | null;
  overallScore: number | null;
  complianceScore: number | null;
  efficiencyScore: number | null;
  safetyScore: number | null;
  kpiMeasurements?: TriggerKpiMeasurement[];
  cameraName?: string | null;
  templateName?: string | null;
  expectedKpi?: { id: string; code: string | null; name: string | null } | null;
  kpiRuns?: TriggerKpiRun[];
  kpiRunning?: boolean;
  /**
   * Phase 41B (§5.5): a presigned URL of the trigger's head-blurred clip, or
   * null when it has none. The previous API omits it (S16).
   */
  videoUrl?: string | null;
}

const NO_WHY: KpiWhy = { shape: 'none' };

// Status config
const statusConfig: Record<string, { label: string; color: string; icon: typeof Clock }> = {
  pending: { label: 'Pending', color: 'bg-gray-100 text-gray-600', icon: Clock },
  pulling_video: { label: 'Pulling Video', color: 'bg-blue-50 text-blue-600', icon: Video },
  analyzing: { label: 'Analyzing', color: 'bg-amber-50 text-amber-600', icon: Loader2 },
  completed: { label: 'Completed', color: 'bg-emerald-50 text-emerald-600', icon: CheckCircle2 },
  failed: { label: 'Failed', color: 'bg-red-50 text-red-600', icon: XCircle },
  pending_review: { label: 'Awaiting Operator ID', color: 'bg-orange-50 text-orange-600', icon: AlertTriangle },
  pending_section_review: { label: 'Awaiting Section', color: 'bg-amber-50 text-amber-600', icon: Layers },
};

const triggerTypeLabels: Record<string, string> = {
  manual: 'Manual',
  scheduled: 'Scheduled',
  activity: 'Activity',
  event: 'Event',
};

/** The colour and icon of each kpiRunBadge tone (lib/kpi-format.ts). */
const KPI_RUN_BADGE_STYLES: Record<KpiRunBadgeTone, { color: string; icon: typeof Clock }> = {
  green: { color: statusConfig.completed.color, icon: CheckCircle2 },
  blue: { color: 'bg-blue-50 text-blue-600', icon: Loader2 },
  red: { color: statusConfig.failed.color, icon: XCircle },
  amber: { color: 'bg-amber-50 text-amber-600', icon: AlertTriangle },
  grey: { color: 'bg-gray-100 text-gray-600', icon: CircleDashed },
};

/**
 * Spec S18: a completed KPI-only trigger's badge shows its KPI run, so it never
 * reads "Completed" beside a result that is still processing, failed or
 * missing. Display only; the trigger's status is unchanged. kpiRunBadge's
 * switch reads a state this page does not know as "No result" (S16).
 */
function kpiRunStatus(state: string | undefined): (typeof statusConfig)[string] {
  const { label, tone } = kpiRunBadge(state);
  return { label, ...KPI_RUN_BADGE_STYLES[tone] };
}

/**
 * The back link (spec §4.2.1). A safe in-app `from` that the caller passed in
 * router state wins (KPI Analytics passes its own URL); otherwise a KPI-only
 * trigger goes to its KPI on KPI Analytics, and anything else to /assessments.
 */
function backLink(
  from: string | null,
  kpiOnly: { code: string | null } | null
): { to: string; label: string } {
  if (from !== null) {
    // React Router matches paths case-insensitively, so the label does too
    const route = from.split(/[?#]/, 1)[0].replace(/\/+$/, '').toLowerCase();
    const label =
      route === '/analytics/kpi'
        ? 'Back to KPI Analytics'
        : route === '/assessments' || route.startsWith('/assessments/')
          ? 'Back to assessments'
          : 'Back';
    return { to: from, label };
  }
  if (kpiOnly !== null) {
    return {
      to: kpiOnly.code
        ? `/analytics/kpi?code=${encodeURIComponent(kpiOnly.code)}`
        : '/analytics/kpi',
      label: 'Back to KPI Analytics',
    };
  }
  return { to: '/assessments', label: 'Back to assessments' };
}

// Score gauge component
function ScoreGauge({ score, label, size = 'lg' }: { score: number | null; label: string; size?: 'sm' | 'lg' }) {
  if (score === null || score === undefined) return null;

  const colors = getScoreColorSet(score);
  const circumference = 2 * Math.PI * 40;
  const offset = circumference - (score / 100) * circumference;

  if (size === 'sm') {
    return (
      <div className="flex flex-col items-center gap-1">
        <div className={`text-lg font-bold ${colors.text}`}>{Math.round(score)}</div>
        <div className="text-xs text-gray-500">{label}</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative w-24 h-24">
        <svg className="w-24 h-24 -rotate-90" viewBox="0 0 100 100">
          <circle cx="50" cy="50" r="40" fill="none" stroke="currentColor" strokeWidth="8" className="text-gray-100" />
          <circle
            cx="50" cy="50" r="40" fill="none" stroke="currentColor" strokeWidth="8"
            className={colors.ring}
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className={`text-xl font-bold ${colors.text}`}>{Math.round(score)}</span>
        </div>
      </div>
      <span className="text-xs font-semibold text-gray-600">{label}</span>
    </div>
  );
}

// Threshold badge
function ThresholdBadge({ status }: { status: string }) {
  const config: Record<string, string> = {
    normal: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    warning: 'bg-amber-50 text-amber-700 border-amber-200',
    critical: 'bg-red-50 text-red-700 border-red-200',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${config[status] ?? 'bg-gray-50 text-gray-600 border-gray-200'}`}>
      {status}
    </span>
  );
}

/**
 * Phase 41A (spec §4.2.1, §4.4.1): one KPI run. A saved run shows its value, its
 * status (badge, or the grey "Not observed" / "Can't assess" / "No value" pill
 * for a result with no value), the one-line descriptor and the Why panel, open
 * by default. Any other run shows kpiRunMessage's wording of its state and
 * reason, e.g. "Failed: the analysis job failed."; a running one with a
 * spinner. A state this page does not know (S16), or a saved run whose result
 * is missing, reads "No KPI result was recorded for this run."
 */
function KpiRunEntry({
  run,
  measurement,
  code,
  suppressBadge,
  clipStart,
  timeZone,
}: {
  run: TriggerKpiRun;
  measurement: TriggerKpiMeasurement | null;
  code: string | null;
  suppressBadge: boolean;
  clipStart: string | null;
  timeZone: string | undefined;
}) {
  const [whyOpen, setWhyOpen] = useState(true);
  // No name or code: a run of a KPI this org cannot see (spec §4.4.7)
  const name = run.kpiName ?? measurement?.kpiName ?? code ?? 'Unknown KPI';
  const title = (
    <div className="min-w-0">
      <p className="text-sm font-semibold text-gray-800">{name}</p>
      {code && <p className="text-xs text-gray-500">{code}</p>}
    </div>
  );

  if (run.state === 'running') {
    return (
      <div className="space-y-2 rounded-lg border border-gray-100 px-4 py-3">
        {title}
        <p className="flex items-center gap-2 text-sm text-blue-700">
          <Loader2 size={14} className="shrink-0 animate-spin" />
          {kpiRunMessage(run)}
        </p>
      </div>
    );
  }

  if (run.state !== 'saved' || measurement === null) {
    return (
      <div className="space-y-2 rounded-lg border border-gray-100 px-4 py-3">
        {title}
        <p className="text-sm text-gray-600">{kpiRunMessage(run)}</p>
      </div>
    );
  }

  const why = measurement.why ?? NO_WHY;
  const thresholdStatus = measurement.thresholdStatus ?? null;
  const observationStatus = rowObservationStatus(measurement);
  // No value: the grey pill alone, as in KPI Analytics. The Why panel keeps the detail.
  const headline = measurement.primaryValue === null ? null : whyHeadline(why, thresholdStatus);
  const panelId = `kpi-why-${run.kpiDefinitionId}`;

  return (
    <div className="space-y-3 rounded-lg border border-gray-100 px-4 py-3">
      <div className="flex items-start justify-between gap-4">
        {title}
        <div className="flex flex-col items-end gap-1 text-right">
          <div className="flex items-center gap-3">
            {measurement.primaryValue !== null && (
              <span className="text-sm font-bold tabular-nums text-gray-900">
                {formatKpiValue(
                  measurement.primaryValue,
                  measurement.unit,
                  measurement.displayFormat
                )}
              </span>
            )}
            {suppressBadge && measurement.primaryValue !== null ? (
              <Link
                to={`/analytics/kpi?code=${encodeURIComponent(code ?? '')}&view=cumulative`}
                className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600 hover:bg-gray-200"
                title="Per-row threshold doesn't apply — this KPI accumulates across the shift window."
              >
                View cumulative
                <ExternalLink size={10} />
              </Link>
            ) : measurement.primaryValue === null ? (
              // Spec D3: a saved result with no value has no status
              <span className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs font-semibold text-gray-600">
                {noValueLabel(observationStatus)}
              </span>
            ) : (
              thresholdStatus && <ThresholdBadge status={thresholdStatus} />
            )}
          </div>
          {/* G-21: what the status rests on */}
          {headline && <p className="text-xs text-gray-500">{headline}</p>}
        </div>
      </div>

      {hasWhyPanel(why) && (
        <div className="space-y-2">
          <button
            type="button"
            aria-expanded={whyOpen}
            aria-controls={panelId}
            onClick={() => setWhyOpen((open) => !open)}
            className="inline-flex items-center gap-1 text-sm font-medium text-gray-600 hover:text-gray-900"
          >
            <ChevronDown
              size={14}
              className={`transition-transform ${whyOpen ? 'rotate-180' : ''}`}
            />
            {whyOpen ? 'Hide why' : 'Show why'}
          </button>
          <div id={panelId} hidden={!whyOpen} className="rounded-lg bg-gray-50 p-4">
            <KpiWhyPanel
              why={why}
              observationStatus={observationStatus}
              thresholdStatus={thresholdStatus}
              clipStart={clipStart}
              timeZone={timeZone}
            />
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Phase 41B (§5.6): the trigger's head-blurred clip. It renders below the KPI
 * results, so the PPE caption's "the reason above" holds. `videoUrl` is the
 * API's presigned URL of the trigger's engine-v2 blur, or null when it has
 * none; the previous API omits it (S16).
 * - A URL: the player, with the PPE caption when a saved result has a PPE Why.
 * - null, and a saved result is warning or critical: "isn't available yet".
 * - Otherwise, including an omitted `videoUrl`: nothing.
 */
function TriggerVideo({
  videoUrl,
  measurements,
}: {
  videoUrl: string | null | undefined;
  measurements: TriggerKpiMeasurement[];
}) {
  // Pinned to the first URL seen, including one that a later poll brings. Each
  // poll (5 s while the trigger awaits review, 10 s while its KPI runs)
  // presigns a new URL, and a new src would reload the video mid-play.
  const [src, setSrc] = useState(videoUrl || null);
  if (src === null && videoUrl) setSrc(videoUrl);
  // Nothing refetches after a playback error (an expired URL, a missing
  // object): the note asks for a reload, so no retry loop can start (EM M-1)
  const [failed, setFailed] = useState(false);

  // Each `kpiMeasurements` item is a saved result (at most one per KPI)
  const flagged = measurements.some((m) => isFlagged(m.thresholdStatus));
  const ppe = measurements.some((m) => m.why?.shape === 'ppe');
  if (src === null && !(videoUrl === null && flagged)) return null;

  return (
    <div className="space-y-4 rounded-2xl border border-gray-100 bg-white p-6 shadow-sm lg:col-span-2">
      <div className="flex items-center gap-2">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-guild-green/10">
          <Video size={16} className="text-guild-green" />
        </div>
        <h3 className="font-bold text-gray-900">Video (heads blurred)</h3>
      </div>

      {src === null || failed ? (
        <p className="flex items-center gap-2 rounded-xl border border-gray-100 bg-gray-50 px-4 py-3 text-sm text-gray-600">
          <VideoOff size={16} className="shrink-0 text-gray-400" />
          {src === null
            ? "Blurred video for this result isn't available yet."
            : "Blurred video isn't available. Reload the page to try again."}
        </p>
      ) : (
        <>
          <div className="overflow-hidden rounded-xl bg-black">
            {/* No crossOrigin: a CORS-mode request would depend on the
                bucket's CORS rules, and nothing reads the pixels */}
            <video
              src={src}
              controls
              preload="metadata"
              playsInline
              onError={() => setFailed(true)}
              className="aspect-video w-full"
            />
          </div>
          {ppe && (
            <p className="text-xs text-gray-500">
              Glasses and ear plugs fall inside the blurred area; the reason above is the evidence.
            </p>
          )}
        </>
      )}
    </div>
  );
}

export function TriggerDetailPage() {
  useDocumentTitle('Trigger Detail');
  const { id } = useParams<{ id: string }>();
  const { data: trigger, isLoading } = useTriggerDetail(id ?? null);
  // Phase 30 Wave 5 §7.1: lookup `aggregationType` per kpiCode so badges can be
  // suppressed for accumulating KPIs (sum/count). Cached 5 min by TanStack.
  const { data: kpiDefinitions } = useKpiDefinitions();
  // Phase 41A (D5): times read in the org's zone. While /api/me loads, or against
  // an API without `orgTimezone`, they read in the browser's zone, labelled as such.
  const { data: currentUser } = useCurrentUser();
  const orgTimezone = currentUser?.orgTimezone;
  // Phase 41A: where the user came from, when the caller passed it (KPI Analytics
  // does). Router state can hold anything, so only a safe in-app path is used
  // (lib/return-to.ts): React Router falls back to location.assign for a
  // cross-origin URL, which would make this an open redirect.
  const location = useLocation();
  const from = (location.state as { from?: unknown } | null)?.from;
  const safeFrom = isSafeInAppPath(from) ? from : null;

  const aggregationByCode = useMemo<Map<string, KpiAggregation | null>>(() => {
    const m = new Map<string, KpiAggregation | null>();
    for (const d of kpiDefinitions ?? []) m.set(d.code, d.aggregationType);
    return m;
  }, [kpiDefinitions]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 size={32} className="animate-spin text-guild-green" />
      </div>
    );
  }

  // Not found only when there is no trigger to show. A failed refetch keeps the
  // last data (TanStack Query), so a failed poll (the KPI poll runs every 10 s
  // for up to 60 minutes) leaves the loaded page in place.
  if (!trigger) {
    const notFoundBack = backLink(safeFrom, null);
    return (
      <div className="text-center py-24 space-y-3">
        <XCircle size={40} className="mx-auto text-red-400" />
        <h2 className="text-lg font-semibold text-gray-900">Trigger not found</h2>
        <Link to={notFoundBack.to} className="text-sm text-guild-green hover:underline">
          {notFoundBack.label}
        </Link>
      </div>
    );
  }

  const t = trigger as unknown as TriggerData;
  const status = t.status ?? 'pending';
  const isProcessing = status === 'pending' || status === 'pulling_video' || status === 'analyzing';
  const isPendingReview = status === 'pending_review';

  // Phase 41A: every new field is read through a default (S16)
  const kpiRuns = t.kpiRuns ?? [];
  const expectedKpi = t.expectedKpi ?? null;
  const cameraName = t.cameraName ?? null;
  const templateName = t.templateName ?? null;
  // A run's result is joined by KPI, never by position; the first result per KPI
  // is the one the API turned into its saved run
  const measurementByKpi = new Map<string, TriggerKpiMeasurement>();
  for (const measurement of t.kpiMeasurements ?? []) {
    if (measurement.kpiDefinitionId && !measurementByKpi.has(measurement.kpiDefinitionId)) {
      measurementByKpi.set(measurement.kpiDefinitionId, measurement);
    }
  }

  // KPI-only: the trigger runs one KPI and no SOP
  const kpiOnly = expectedKpi !== null && !t.sopId ? expectedKpi : null;
  const statusInfo =
    kpiOnly !== null && status === 'completed'
      ? kpiRunStatus(kpiRuns.find((run) => run.kpiDefinitionId === kpiOnly.id)?.state)
      : (statusConfig[status] ?? statusConfig.pending);
  const StatusIcon = statusInfo.icon;
  const back = backLink(safeFrom, kpiOnly);
  const filmed = formatFootageWindow(t.startTime, t.endTime, orgTimezone);

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Back link */}
      <Link to={back.to} className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors">
        <ArrowLeft size={16} />
        {back.label}
      </Link>

      {/* Header */}
      <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-50">
          <div className="flex items-start justify-between">
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <h1 className="text-xl font-bold text-gray-900">
                  Video Analysis Results
                </h1>
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${statusInfo.color}`}>
                  <StatusIcon size={12} className={StatusIcon === Loader2 ? 'animate-spin' : ''} />
                  {statusInfo.label}
                </span>
              </div>
              <div className="flex items-center gap-4 text-sm text-gray-500">
                {/* Phase 41A (G-18): the clip window, in the org's zone */}
                <span className="flex items-center gap-1.5">
                  <Clock size={14} />
                  Filmed {filmed ?? '—'}
                </span>
                {t.triggerType && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-gray-100 text-xs font-medium text-gray-600">
                    {triggerTypeLabels[t.triggerType] ?? t.triggerType}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Meta info row */}
          <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-gray-600">
            {/* Phase 41A (G-17): names, not ids. The camera id is the fallback
                only when no camera row resolves, or against the previous API */}
            {(cameraName ?? t.cameraId) && (
              <span className="flex items-center gap-1.5">
                <Camera size={14} className="text-gray-400" />
                Camera: {cameraName ?? t.cameraId}
                {typeof t.sourceNumber === 'number' && ` · Lens ${t.sourceNumber}`}
              </span>
            )}
            {templateName && (
              <span className="flex items-center gap-1.5">
                <ClipboardList size={14} className="text-gray-400" />
                Template: {templateName}
              </span>
            )}
            {expectedKpi && (expectedKpi.name ?? expectedKpi.code) && (
              <span className="flex items-center gap-1.5">
                <Target size={14} className="text-gray-400" />
                KPI: {expectedKpi.name ?? expectedKpi.code}
              </span>
            )}
            {t.operatorId && (
              <span className="flex items-center gap-1.5">
                <User size={14} className="text-gray-400" />
                Operator identified
              </span>
            )}
            {t.sopId && (
              <span className="flex items-center gap-1.5">
                <FileText size={14} className="text-gray-400" />
                SOP linked
              </span>
            )}
          </div>
        </div>

        {/* Processing indicator */}
        {isProcessing && (
          <div className="px-6 py-4 bg-blue-50/50 border-b border-blue-100">
            <div className="flex items-center gap-3">
              <Loader2 size={16} className="animate-spin text-blue-500" />
              <div>
                <p className="text-sm font-medium text-blue-700">
                  {status === 'pending' && 'Waiting to start...'}
                  {status === 'pulling_video' && 'Pulling video from camera...'}
                  {status === 'analyzing' && 'AI is analyzing the video...'}
                </p>
                <p className="text-xs text-blue-500 mt-0.5">This page updates automatically</p>
              </div>
            </div>
          </div>
        )}

        {/* Pending operator review banner */}
        {isPendingReview && (
          <div className="px-6 py-4 bg-orange-50/50 border-b border-orange-100">
            <div className="flex items-center gap-3">
              <AlertTriangle size={16} className="text-orange-500" />
              <div>
                <p className="text-sm font-medium text-orange-700">
                  Operator identification required
                </p>
                <p className="text-xs text-orange-500 mt-0.5">
                  Video analysis is complete. Resolve the pending identification to create the assessment.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Pending section review banner */}
        {status === 'pending_section_review' && (
          <div className="px-6 py-4 bg-amber-50/50 border-b border-amber-100">
            <div className="flex items-center gap-3">
              <Layers size={16} className="text-amber-500" />
              <div>
                <p className="text-sm font-medium text-amber-700">
                  Awaiting Section Assignment
                </p>
                <p className="text-xs text-amber-500 mt-0.5">
                  The video could not be automatically matched to an SOP section.
                </p>
                {t.observationSummary && (
                  <div className="mt-3 p-3 bg-gray-50 rounded-lg text-sm text-gray-700">
                    <p className="font-medium mb-1">AI Observation:</p>
                    <p className="whitespace-pre-wrap">{t.observationSummary}</p>
                  </div>
                )}
                <Link
                  to="/assessments/section-review"
                  className="inline-flex items-center mt-3 text-sm font-medium text-[#00674a] hover:underline"
                >
                  Go to Job Review Queue →
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Results Grid */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* SOP Assessment Card */}
        {(t.assessmentId || (isPendingReview && t.overallScore !== null)) && (
          <div className="rounded-2xl border border-gray-100 bg-white shadow-sm p-6 space-y-4">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-guild-green/10">
                <BarChart3 size={16} className="text-guild-green" />
              </div>
              <h3 className="font-bold text-gray-900">SOP Assessment</h3>
            </div>

            {/* Score gauges */}
            <div className="flex items-center justify-around py-2">
              <ScoreGauge score={t.overallScore} label="Overall" />
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-2">
                  <Shield size={14} className="text-purple-500" />
                  <ScoreGauge score={t.complianceScore} label="Compliance" size="sm" />
                </div>
                <div className="flex items-center gap-2">
                  <Zap size={14} className="text-blue-500" />
                  <ScoreGauge score={t.efficiencyScore} label="Efficiency" size="sm" />
                </div>
                <div className="flex items-center gap-2">
                  <AlertTriangle size={14} className="text-amber-500" />
                  <ScoreGauge score={t.safetyScore} label="Safety" size="sm" />
                </div>
              </div>
            </div>

            {/* Detected Job */}
            {t.detectedSectionId && (
              <div className="rounded-lg bg-gray-50 border border-gray-100 px-4 py-2.5">
                <p className="text-xs text-gray-500">Detected Job</p>
                <p className="text-sm font-semibold text-gray-800">
                  {t.detectedSectionTitle || 'Section detected'}
                  {t.detectedSectionConfidence && (
                    <span className="text-xs text-gray-400 ml-2">
                      ({Math.round(t.detectedSectionConfidence! * 100)}% confidence)
                    </span>
                  )}
                </p>
              </div>
            )}

            {/* Phase 30.1.4: View Full Assessment CTA — promoted to a proper
                button so it's the obvious next action once the trigger has
                resolved to an assessment. */}
            {t.assessmentId ? (
              <Link
                to={`/assessments/${t.assessmentId}`}
                className="
                  inline-flex items-center gap-2 rounded-xl
                  bg-guild-green px-4 py-2.5
                  text-sm font-semibold text-white
                  shadow-md shadow-guild-green/20
                  transition-all duration-200
                  hover:bg-guild-green/90 hover:shadow-lg hover:shadow-guild-green/30
                  active:scale-[0.98]
                "
              >
                View Full Assessment →
              </Link>
            ) : (
              <p className="text-xs text-gray-400 italic">Assessment will be created after operator is identified</p>
            )}
          </div>
        )}

        {/* KPI Results Card. Phase 41A: one entry per KPI run (`kpiRuns`). The
            API sends a run for each saved result; one for each other KPI whose
            run recorded an outcome (low confidence, failed, never queued, not
            run, or finished before run tracking began), with its reason; and,
            on a completed trigger whose own KPI has neither, one more for that
            KPI: `running` until 60 minutes after the trigger completed (the KPI
            worker writes its result after the trigger completes), then
            `no_result`. */}
        {kpiRuns.length > 0 && (
          <div className="rounded-2xl border border-gray-100 bg-white shadow-sm p-6 space-y-4 lg:col-span-2">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50">
                <Target size={16} className="text-blue-600" />
              </div>
              <h3 className="font-bold text-gray-900">KPI Results</h3>
            </div>

            <div className="space-y-3">
              {kpiRuns.map((run) => {
                const measurement = measurementByKpi.get(run.kpiDefinitionId) ?? null;
                // Phase 30 Wave 5 §7.1: sum/count KPIs roll up across a shift
                // window; per-row badges mislead. Render a neutral pill that
                // links to the cumulative view instead.
                const code = run.kpiCode ?? measurement?.kpiCode ?? null;
                const aggregation = code === null ? null : (aggregationByCode.get(code) ?? null);
                return (
                  <KpiRunEntry
                    key={run.kpiDefinitionId}
                    run={run}
                    measurement={measurement}
                    code={code}
                    suppressBadge={aggregation === 'sum' || aggregation === 'count'}
                    clipStart={t.startTime ?? null}
                    timeZone={orgTimezone}
                  />
                );
              })}
            </div>
          </div>
        )}

        {/* Phase 41B: the head-blurred clip, below the KPI results. Keyed by
            trigger: React Router keeps this page mounted across trigger ids,
            and one trigger's pinned URL must not play on another's page. */}
        <TriggerVideo key={t.id} videoUrl={t.videoUrl} measurements={t.kpiMeasurements ?? []} />

        {/* Observation Card */}
        {t.observationSummary && (
          <div className="rounded-2xl border border-gray-100 bg-white shadow-sm p-6 space-y-4 lg:col-span-2">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-purple-50">
                <MessageSquare size={16} className="text-purple-600" />
              </div>
              <h3 className="font-bold text-gray-900">Observation</h3>
            </div>

            {/* Manager's question */}
            {t.managerNotes && (
              <div className="border-l-4 border-guild-green/30 pl-4 py-2">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Manager's Question</p>
                <p className="text-sm text-gray-700 italic">"{t.managerNotes}"</p>
              </div>
            )}

            {/* AI response */}
            <div className="rounded-xl bg-gray-50 border border-gray-100 p-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">AI Analysis</p>
              <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">
                {t.observationSummary}
              </p>
            </div>
          </div>
        )}

        {/* Error Card */}
        {t.errorMessage && t.status === 'failed' && (
          <div className="rounded-2xl border border-red-100 bg-red-50 p-6 space-y-2 lg:col-span-2">
            <div className="flex items-center gap-2">
              <XCircle size={18} className="text-red-500" />
              <h3 className="font-bold text-red-700">Error</h3>
            </div>
            <p className="text-sm text-red-600">{t.errorMessage}</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default TriggerDetailPage;
